import React, { useState } from 'react';
import { toast } from 'react-toastify';
import SelectTwo, { SelectTwoMapperOptions } from '../../../../components/SelectTwo';
import { ExchangeRateDateEnum } from '../../../../app/shared/enums';
import { TransportationOrderInvoicingShowDataForm } from '../../../../app/models/TransportationOrderInvoicing';
import { PaymentMethod } from '../../../../app/models/PaymentMethod';
import { QuoteReferenceDataForm } from '../../../../app/models/Quote';
import { Unit } from '../../../../app/models/Unit';
import { ExchangeRateDate } from '../../../../app/models/ExchangeRateDate';
import { ORDER_PAYMENTS_COUNT_OPTIONS } from '../../../../app/models/Order';
import { validatePaymentsDistribution } from '../../../../app/shared/paymentsDistribution';
import useOrderService from '../../../../app/services/hooks/useOrderService';
import { ServiceResponse } from '../../../../app/services/shared/interfaces';

interface Props {
  transportationOrderInvoicingDataForm: TransportationOrderInvoicingShowDataForm;
  setTransportationOrderInvoicingDataForm: (form: TransportationOrderInvoicingShowDataForm) => void;
  orderId?: number;
  paymentMethods?: PaymentMethod[];
  references?: QuoteReferenceDataForm[];
  units?: Unit[];
  exchangeRateDate?: ExchangeRateDate[];
  errorFields?: Record<string, unknown>;
  onSaved?: () => void;
}

const TransportationOrderInvoicingShowForm: React.FC<Props> = ({
  transportationOrderInvoicingDataForm,
  setTransportationOrderInvoicingDataForm,
  orderId,
  paymentMethods = [],
  references = [],
  units = [],
  exchangeRateDate = [],
  errorFields: _errorFields,
  onSaved,
}) => {
  void _errorFields;

  const { updateOrderInvoicing, fetchingUpdateOrderInvoicing } = useOrderService();

  const [localUseStandardWeight, setLocalUseStandardWeight] = useState<boolean>(
    !!transportationOrderInvoicingDataForm.use_standard_weight
  );

  const showPrelimFixed =
    transportationOrderInvoicingDataForm.preliminary_exchange_rate_date_reference?.id ===
    ExchangeRateDateEnum.FECHA_FIJA;

  const showFinalFixed =
    transportationOrderInvoicingDataForm.final_exchange_rate_date_reference?.id ===
    ExchangeRateDateEnum.FECHA_FIJA;

  const maxPaymentsAllowed = Number(transportationOrderInvoicingDataForm.max_payments_allowed ?? 1);
  const paymentsCount = Number(transportationOrderInvoicingDataForm.payments_count ?? 1);
  const showPaymentsDistribution = paymentsCount === 2;
  // Se incluye el valor vigente para no ocultarlo si la cotización bajó su tope.
  const paymentsCountOptions = ORDER_PAYMENTS_COUNT_OPTIONS.filter(
    option => option.id <= Math.max(maxPaymentsAllowed, paymentsCount)
  );

  const handlePaymentsCountChange = (value: number) => {
    setTransportationOrderInvoicingDataForm({
      ...transportationOrderInvoicingDataForm,
      payments_count: value,
      ...(value === 2
        ? {}
        : { payment_percentage_1: null, payment_percentage_2: null }),
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTransportationOrderInvoicingDataForm({
      ...transportationOrderInvoicingDataForm,
      [name]: value,
    });
  };

  const handleSelect = (name: string, value: unknown) => {
    setTransportationOrderInvoicingDataForm({
      ...transportationOrderInvoicingDataForm,
      [name]: value,
    });
  };

  const handleSave = () => {
    if (!orderId) return;

    const form = transportationOrderInvoicingDataForm;
    const isPrelimFixed = form.preliminary_exchange_rate_date_reference?.id === ExchangeRateDateEnum.FECHA_FIJA;
    const isFinalFixed = form.final_exchange_rate_date_reference?.id === ExchangeRateDateEnum.FECHA_FIJA;

    const distributionError = validatePaymentsDistribution(
      paymentsCount,
      form.payment_percentage_1,
      form.payment_percentage_2
    );
    if (distributionError) {
      toast.error(distributionError);
      return;
    }

    updateOrderInvoicing(
      orderId,
      {
        payment_method_id: form.payment_method_id,
        reference: form.reference_id,
        days_count: form.days_count,
        payments_count: paymentsCount,
        payment_percentage_1: paymentsCount === 2 ? form.payment_percentage_1 : null,
        payment_percentage_2: paymentsCount === 2 ? form.payment_percentage_2 : null,
        use_standard_weight: localUseStandardWeight,
        standard_weight_value: form.standard_weight_value,
        standard_weight_unit_id: form.standard_weight_unit_id,
        standard_weight_lower_limit: form.standard_weight_lower_limit,
        standard_weight_lower_limit_unit_id: form.standard_weight_lower_limit_unit_id,
        preliminary_exchange_rate_date_reference: form.preliminary_exchange_rate_date_reference?.id,
        final_exchange_rate_date_reference: form.final_exchange_rate_date_reference?.id,
        ...(isPrelimFixed
          ? { preliminary_fixed_date: form.preliminary_fixed_date }
          : {
              preliminary_days: form.preliminary_days,
              preliminary_months: form.preliminary_months,
              preliminary_year: form.preliminary_year,
            }),
        ...(isFinalFixed
          ? { final_fixed_date: form.final_fixed_date }
          : {
              final_days: form.final_days,
              final_months: form.final_months,
              final_year: form.final_year,
            }),
      },
      {
        onSuccess: (res: ServiceResponse) => {
          const d = res?.data as Record<string, unknown> | undefined;
          setTransportationOrderInvoicingDataForm({
            ...transportationOrderInvoicingDataForm,
            use_standard_weight:
              typeof d?.use_standard_weight === 'boolean'
                ? d.use_standard_weight
                : localUseStandardWeight,
            standard_weight_value:
              (d?.standard_weight_value as TransportationOrderInvoicingShowDataForm['standard_weight_value']) ??
              transportationOrderInvoicingDataForm.standard_weight_value,
            standard_weight_unit_id:
              (d?.standard_weight_unit_id as TransportationOrderInvoicingShowDataForm['standard_weight_unit_id']) ??
              transportationOrderInvoicingDataForm.standard_weight_unit_id,
            standard_weight_lower_limit:
              (d?.standard_weight_lower_limit as TransportationOrderInvoicingShowDataForm['standard_weight_lower_limit']) ??
              transportationOrderInvoicingDataForm.standard_weight_lower_limit,
            standard_weight_lower_limit_unit_id:
              (d?.standard_weight_lower_limit_unit_id as TransportationOrderInvoicingShowDataForm['standard_weight_lower_limit_unit_id']) ??
              transportationOrderInvoicingDataForm.standard_weight_lower_limit_unit_id,
            preliminary_exchange_rate_date: (d?.preliminary_exchange_rate_date as string) ?? transportationOrderInvoicingDataForm.preliminary_exchange_rate_date,
            final_exchange_rate_date: (d?.final_exchange_rate_date as string) ?? transportationOrderInvoicingDataForm.final_exchange_rate_date,
            prelim_rate: (d?.prelim_rate as unknown as number) ?? transportationOrderInvoicingDataForm.prelim_rate,
            final_rate: (d?.final_rate as unknown as number) ?? transportationOrderInvoicingDataForm.final_rate,
          });
          toast.success('Facturación del pedido actualizada correctamente.');
          if (onSaved) onSaved();
        },
        onError: (res: { message?: string }) => {
          toast.error(res?.message || 'Error al actualizar la facturación del pedido.');
        },
      }
    );
  };

  const standardWeightEnabled = localUseStandardWeight;

  return (
    <div className="container-fluid">
      {/* 1. Forma de pago y condición de pago (editables) */}
      <div className="row mb-3">
        <div className="col-md-6">
          <label>Forma de Pago</label>
          {orderId ? (
            <SelectTwo
              name="payment_method_id"
              inputValue={transportationOrderInvoicingDataForm.payment_method_id ?? undefined}
              options={SelectTwoMapperOptions(paymentMethods, 'id', ['name'])}
              onChange={v => handleSelect('payment_method_id', v?.value ?? null)}
              placeholder="Seleccione"
            />
          ) : (
            <input
              type="text"
              className="form-control bg-light"
              value={transportationOrderInvoicingDataForm.payment_method || ''}
              readOnly
            />
          )}
        </div>
        <div className="col-md-6">
          <label>Condición de Pago</label>
          <div className="row g-1">
            <div className="col-8">
              {orderId ? (
                <SelectTwo
                  name="reference_id"
                  inputValue={transportationOrderInvoicingDataForm.reference_id ?? undefined}
                  options={SelectTwoMapperOptions(references)}
                  onChange={v => handleSelect('reference_id', v?.value ?? null)}
                  placeholder="Seleccione Referencia"
                />
              ) : (
                <input
                  type="text"
                  className="form-control bg-light"
                  value={transportationOrderInvoicingDataForm.reference || ''}
                  readOnly
                  placeholder="Referencia"
                />
              )}
            </div>
            <div className="col-4">
              <input
                type="number"
                className="form-control"
                name="days_count"
                value={transportationOrderInvoicingDataForm.days_count ?? ''}
                onChange={handleChange}
                readOnly={!orderId}
                placeholder="Días"
              />
            </div>
          </div>
        </div>
      </div>

      {/* 1.1 Cantidad de pagos y distribución porcentual */}
      <div className="row mb-3">
        <div className="col-md-6">
          <label>Cantidad de Pagos</label>
          {orderId ? (
            <SelectTwo
              name="payments_count"
              inputValue={paymentsCount}
              options={SelectTwoMapperOptions(paymentsCountOptions, 'id', ['name'])}
              onChange={v => handlePaymentsCountChange(Number(v?.value ?? 1))}
              placeholder="Seleccione"
            />
          ) : (
            <input
              type="text"
              className="form-control bg-light"
              value={paymentsCount === 2 ? '2 pagos' : '1 pago'}
              readOnly
            />
          )}
          {maxPaymentsAllowed < 2 && (
            <div className="form-text">
              La cotización asociada solo permite 1 pago.
            </div>
          )}
        </div>

        {showPaymentsDistribution && (
          <>
            <div className="col-md-3">
              <label>Porcentaje Pago 1 (%)</label>
              <input
                type="number"
                className="form-control"
                name="payment_percentage_1"
                value={transportationOrderInvoicingDataForm.payment_percentage_1 ?? ''}
                onChange={handleChange}
                readOnly={!orderId}
                min={0}
                max={100}
                step="0.01"
                placeholder="Ej: 50"
              />
            </div>
            <div className="col-md-3">
              <label>Porcentaje Pago 2 (%)</label>
              <input
                type="number"
                className="form-control"
                name="payment_percentage_2"
                value={transportationOrderInvoicingDataForm.payment_percentage_2 ?? ''}
                onChange={handleChange}
                readOnly={!orderId}
                min={0}
                max={100}
                step="0.01"
                placeholder="Ej: 50"
              />
            </div>
            <div className="col-12">
              <div className="form-text">
                La suma de ambos porcentajes debe ser exactamente 100%.
              </div>
            </div>
          </>
        )}
      </div>

      {/* 2. Moneda y fuente tipo de cambio (bloqueadas) */}
      <div className="row mb-3">
        <div className="col-md-6">
          <label>Moneda</label>
          <input
            type="text"
            className="form-control bg-light"
            value={transportationOrderInvoicingDataForm.currency || ''}
            readOnly
          />
        </div>
        <div className="col-md-6">
          <label>Fuente Tipo de Cambio</label>
          <input
            type="text"
            className="form-control bg-light"
            value={transportationOrderInvoicingDataForm.exchange_rate_date_source || ''}
            readOnly
          />
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-12 mb-2">
          <div className="form-check form-switch mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="use_standard_weight_switch"
              checked={localUseStandardWeight}
              onChange={(e) => setLocalUseStandardWeight(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="use_standard_weight_switch">
              Aplicar peso padrón
            </label>
          </div>
        </div>
        <div className="col-md-6">
          <label className="form-label">Peso padrón</label>
          <input
            type="number"
            className="form-control"
            value={transportationOrderInvoicingDataForm.standard_weight_value ?? ''}
            onChange={e => handleSelect('standard_weight_value', e.target.value)}
            placeholder="Valor"
            disabled={!standardWeightEnabled}
            min={0}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">Unidad peso padrón</label>
          <SelectTwo
            name="standard_weight_unit_id"
            inputValue={transportationOrderInvoicingDataForm.standard_weight_unit_id ?? undefined}
            options={SelectTwoMapperOptions(units, 'id', ['name'])}
            onChange={v => handleSelect('standard_weight_unit_id', v?.value ?? null)}
            placeholder="Seleccione unidad"
            disabled={!standardWeightEnabled}
            isClearable
          />
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-6">
          <label className="form-label">Límite inferior</label>
          <input
            type="number"
            className="form-control"
            value={transportationOrderInvoicingDataForm.standard_weight_lower_limit ?? ''}
            onChange={e => handleSelect('standard_weight_lower_limit', e.target.value)}
            placeholder="Valor"
            disabled={!standardWeightEnabled}
            min={0}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">Unidad límite inferior</label>
          <SelectTwo
            name="standard_weight_lower_limit_unit_id"
            inputValue={transportationOrderInvoicingDataForm.standard_weight_lower_limit_unit_id ?? undefined}
            options={SelectTwoMapperOptions(units, 'id', ['name'])}
            onChange={v => handleSelect('standard_weight_lower_limit_unit_id', v?.value ?? null)}
            placeholder="Seleccione unidad"
            disabled={!standardWeightEnabled}
            isClearable
          />
        </div>
      </div>

      {/* Tipo de cambio preliminar y final (editables; el valor calculado se recibe al Guardar) */}
      <div className="row">
        {/* — Preliminar — */}
        <div className="col-lg-6 mb-3">
          <label>Tipo de Cambio Prov</label>
          {orderId ? (
            <SelectTwo
              name="preliminary_exchange_rate_date_reference"
              inputValue={transportationOrderInvoicingDataForm.preliminary_exchange_rate_date_reference?.id ?? undefined}
              options={SelectTwoMapperOptions(exchangeRateDate)}
              onChange={v =>
                handleSelect(
                  'preliminary_exchange_rate_date_reference',
                  v ? { id: v.value, name: v.label } : null
                )
              }
              placeholder="Seleccione"
            />
          ) : (
            <input
              type="text"
              className="form-control bg-light"
              value={transportationOrderInvoicingDataForm.preliminary_exchange_rate_date_reference_value || ''}
              readOnly
            />
          )}

          {!showPrelimFixed && (
            <div className="row g-2 mt-2">
              <div className="col-3">
                <label className="form-label">Días (Prov)</label>
                <input
                  type="number"
                  className="form-control"
                  name="preliminary_days"
                  value={transportationOrderInvoicingDataForm.preliminary_days ?? ''}
                  onChange={handleChange}
                  readOnly={!orderId}
                />
              </div>
              <div className="col-3">
                <label className="form-label">Meses (Prov)</label>
                <input
                  type="number"
                  className="form-control"
                  name="preliminary_months"
                  value={transportationOrderInvoicingDataForm.preliminary_months ?? ''}
                  onChange={handleChange}
                  readOnly={!orderId}
                />
              </div>
              <div className="col-3">
                <label className="form-label">Año (Prov)</label>
                <input
                  type="number"
                  className="form-control"
                  name="preliminary_year"
                  value={transportationOrderInvoicingDataForm.preliminary_year ?? ''}
                  onChange={handleChange}
                  readOnly={!orderId}
                />
              </div>
            </div>
          )}

          <div className="row g-2 mt-2">
            <div className="col-6">
              <label className="form-label">
                {showPrelimFixed ? 'Fecha Fija (Prov)' : 'Fecha Ref. Tipo Cambio (Prov)'}
              </label>
              {showPrelimFixed && orderId ? (
                <input
                  type="date"
                  className="form-control"
                  name="preliminary_fixed_date"
                  value={transportationOrderInvoicingDataForm.preliminary_fixed_date || ''}
                  onChange={handleChange}
                />
              ) : (
                <input
                  type="date"
                  className="form-control bg-light"
                  value={
                    showPrelimFixed
                      ? transportationOrderInvoicingDataForm.preliminary_fixed_date || ''
                      : transportationOrderInvoicingDataForm.preliminary_exchange_rate_date || ''
                  }
                  readOnly
                />
              )}
            </div>
            <div className="col-6">
              <label className="form-label">Valor Tipo Cambio (Prov)</label>
              <input
                type="text"
                className="form-control bg-light"
                value={
                  transportationOrderInvoicingDataForm.prelim_rate !== undefined &&
                  transportationOrderInvoicingDataForm.prelim_rate !== null
                    ? transportationOrderInvoicingDataForm.prelim_rate
                    : ''
                }
                readOnly
              />
            </div>
          </div>
        </div>

        {/* — Final — */}
        <div className="col-lg-6 mb-3">
          <label>Tipo de Cambio Final</label>
          {orderId ? (
            <SelectTwo
              name="final_exchange_rate_date_reference"
              inputValue={transportationOrderInvoicingDataForm.final_exchange_rate_date_reference?.id ?? undefined}
              options={SelectTwoMapperOptions(exchangeRateDate)}
              onChange={v =>
                handleSelect(
                  'final_exchange_rate_date_reference',
                  v ? { id: v.value, name: v.label } : null
                )
              }
              placeholder="Seleccione"
            />
          ) : (
            <input
              type="text"
              className="form-control bg-light"
              value={transportationOrderInvoicingDataForm.final_exchange_rate_date_reference_value || ''}
              readOnly
            />
          )}

          {!showFinalFixed && (
            <div className="row g-2 mt-2">
              <div className="col-3">
                <label className="form-label">Días (Final)</label>
                <input
                  type="number"
                  className="form-control"
                  name="final_days"
                  value={transportationOrderInvoicingDataForm.final_days ?? ''}
                  onChange={handleChange}
                  readOnly={!orderId}
                />
              </div>
              <div className="col-3">
                <label className="form-label">Meses (Final)</label>
                <input
                  type="number"
                  className="form-control"
                  name="final_months"
                  value={transportationOrderInvoicingDataForm.final_months ?? ''}
                  onChange={handleChange}
                  readOnly={!orderId}
                />
              </div>
              <div className="col-3">
                <label className="form-label">Año (Final)</label>
                <input
                  type="number"
                  className="form-control"
                  name="final_year"
                  value={transportationOrderInvoicingDataForm.final_year ?? ''}
                  onChange={handleChange}
                  readOnly={!orderId}
                />
              </div>
            </div>
          )}

          <div className="row g-2 mt-2">
            <div className="col-6">
              <label className="form-label">
                {showFinalFixed ? 'Fecha Fija (Final)' : 'Fecha Ref. Tipo Cambio (Final)'}
              </label>
              {showFinalFixed && orderId ? (
                <input
                  type="date"
                  className="form-control"
                  name="final_fixed_date"
                  value={transportationOrderInvoicingDataForm.final_fixed_date || ''}
                  onChange={handleChange}
                />
              ) : (
                <input
                  type="date"
                  className="form-control bg-light"
                  value={
                    showFinalFixed
                      ? transportationOrderInvoicingDataForm.final_fixed_date || ''
                      : transportationOrderInvoicingDataForm.final_exchange_rate_date || ''
                  }
                  readOnly
                />
              )}
            </div>
            <div className="col-6">
              <label className="form-label">Valor Tipo Cambio (Final)</label>
              <input
                type="text"
                className="form-control bg-light"
                value={
                  transportationOrderInvoicingDataForm.final_rate !== undefined &&
                  transportationOrderInvoicingDataForm.final_rate !== null
                    ? transportationOrderInvoicingDataForm.final_rate
                    : ''
                }
                readOnly
              />
            </div>
          </div>
        </div>
      </div>

      {orderId && (
        <div className="row mb-3">
          <div className="col-12 d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={fetchingUpdateOrderInvoicing}
            >
              {fetchingUpdateOrderInvoicing ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransportationOrderInvoicingShowForm;
